import { Advantages } from '@/components/astro/new-design-25/Homepage/Advantages.tsx';
import { Banner } from '@/components/astro/new-design-25/Homepage/Banner.tsx';
import { Features } from '@/components/astro/new-design-25/Homepage/Features.tsx';
import { HomepageHero } from '@/components/astro/new-design-25/Homepage/HomepageHero.tsx';
import { ListenIn } from '@/components/astro/new-design-25/Homepage/ListenIn.tsx';
import { SaveTime } from '@/components/astro/new-design-25/Homepage/SaveTime.tsx';
import { TryListening } from '@/components/astro/new-design-25/Homepage/TryListening';
import UniversityBanner from '@/components/astro/new-design-25/Homepage/UniversityBanner';
import { LinksSrOnly } from '@/components/astro/new-design-25/SEO/LinksSrOnly.tsx';
import {
  AppSlider,
  AppSliderMobile,
} from '@/components/astro/new-design-25/ui/AppSlider';
import { type LinkOptions, useRouter } from '@tanstack/react-router';
import { CtaButtonContext } from '@/components/astro/cta-button/cta-button.tsx';

import { FAQButton } from '@/components/astro/new-design-25/ui/FAQ-Button.tsx';
import { Footer } from '@/components/astro/new-design-25/ui/Footer.tsx';
import { NavBar } from '@/components/astro/new-design-25/ui/NavBar.tsx';
import { Testimonials } from '@/components/astro/new-design-25/ui/Testimonials.tsx';
import { TryListeningBanner } from '@/components/astro/new-design-25/ui/TryListeningBanner.tsx';
import { GridBackground } from '@/components/shared-ui/grid-background';
import * as m from '@/paraglide/messages';

/**
 * `ctaLink` retargets EVERY CTA on this page that leads to signup — the nav
 * bar, the hero, the SaveTime block, the demo banner, the app sliders, the
 * testimonials button, the FAQ button and the closing banner. They are one
 * conversion path presented in several places, so an experiment on that
 * path has to move all of them: a visitor who scrolls past the hero and
 * clicks the nav would otherwise land in the treatment arm's denominator
 * having never seen the thing being tested.
 *
 * Delivered through `CtaButtonContext` rather than a prop per section.
 * `<CtaButton>` reads it directly; the plain-link CTAs read it via
 * `useCtaHref`. Omitted, no provider mounts and every CTA resolves the
 * shared default (`DEFAULT_CTA_LINK`).
 */
export function Homepage25Body({
  ctaLink,
}: {
  ctaLink?: LinkOptions | undefined;
}) {
  const router = useRouter();

  const body = (
    <>
      <a
        className='sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4'
        href='#main-content'
      >
        {m.skip_to_main_content()}
      </a>
      <LinksSrOnly />
      <div className='relative'>
        <GridBackground>
          <div className='relative z-10'>
            <NavBar />
            <main id='main-content' tabIndex={-1}>
              <HomepageHero />
            </main>
          </div>
        </GridBackground>
      </div>

      <div
        aria-label='Try Listening demo'
        className='mx-auto mt-4 max-w-[1600px] px-4 sm:px-6 lg:px-8'
        role='region'
      >
        <TryListening>
          <Banner />
        </TryListening>
      </div>

      <UniversityBanner />
      <ListenIn />
      <SaveTime />
      <Advantages />
      <Features />

      {/* <!-- Hide desktop version on mobile --> */}
      <div className='hidden md:block'>
        <AppSlider />
      </div>

      {/* <!-- Hide mobile version on desktop --> */}
      <div className='md:hidden'>
        <AppSliderMobile />
      </div>

      <Testimonials />

      <div role='complementary'>
        <FAQButton />
        <TryListeningBanner />
        <Footer />
      </div>
    </>
  );

  if (ctaLink === undefined) return body;
  // `publicHref`, not `href`: the router's output rewrite (paraglide
  // `localizeUrl`) only applies to the public form, and it is what `Link` and
  // `navigate` emit. `href` is the internal de-localized path — using it would
  // strip the `/es` prefix and drop Spanish visitors into the English funnel,
  // an uncontrolled second variable in the experiment.
  return (
    <CtaButtonContext
      value={{ href: router.buildLocation(ctaLink).publicHref }}
    >
      {body}
    </CtaButtonContext>
  );
}
